/**
 * The anesthesia window (feature 044) — the biller enters four fields, the system
 * stores two instants.
 *
 * Form side: `anesthesiaStartDate`/`anesthesiaStartTime` and
 * `anesthesiaEndDate`/`anesthesiaEndTime`, ISO date `YYYY-MM-DD` and 24-hour `HH:mm`
 * as produced by LabeledDatePicker / LabeledTimePicker.
 *
 * Wire and storage side: `anesthesiaStart` and `anesthesiaEnd`, each a local
 * wall-clock instant formatted `YYYY-MM-DDTHH:mm` — no UTC offset, no trailing `Z`,
 * minute precision. Times are practice-local and MUST NOT be time-zone converted
 * (spec A-004); the string sorts lexicographically, so comparisons need no parsing.
 *
 * A window is stored complete or not at all (FR-018), so {@link composeAnesthesiaWindow}
 * returns `null` unless all four parts are present and well-formed.
 */

/** The four fields the biller edits. */
export interface AnesthesiaWindowFields {
  anesthesiaStartDate: string;
  anesthesiaStartTime: string;
  anesthesiaEndDate: string;
  anesthesiaEndTime: string;
}

/** The two instants that are persisted. */
export interface AnesthesiaWindow {
  anesthesiaStart: string;
  anesthesiaEnd: string;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const TIME_24H = /^([01]\d|2[0-3]):[0-5]\d$/;
// Seconds are tolerated on read and dropped. The service emits minute precision, but any
// producer serializing a plain DateTime yields `...T22:30:00`; rejecting that blanked the
// whole window silently rather than degrading. Writing stays minute-precision.
const INSTANT = /^(\d{4}-\d{2}-\d{2})T([01]\d|2[0-3]):[0-5]\d(?::[0-5]\d)?$/;

/** Joins a date and a time into an instant, or '' when either part is unusable. */
function toInstant(date: string, time: string): string {
  const d = (date ?? '').trim();
  const t = (time ?? '').trim();
  if (!ISO_DATE.test(d) || !TIME_24H.test(t)) return '';
  return `${d}T${t}`;
}

/**
 * Builds the two persisted instants from the four form fields, or `null` when the
 * window is incomplete or malformed — callers persist `null` as "no window" rather
 * than writing half of one.
 */
export function composeAnesthesiaWindow(fields: Partial<AnesthesiaWindowFields>): AnesthesiaWindow | null {
  const anesthesiaStart = toInstant(fields.anesthesiaStartDate ?? '', fields.anesthesiaStartTime ?? '');
  const anesthesiaEnd = toInstant(fields.anesthesiaEndDate ?? '', fields.anesthesiaEndTime ?? '');
  if (!anesthesiaStart || !anesthesiaEnd) return null;
  return { anesthesiaStart, anesthesiaEnd };
}

/**
 * Splits stored instants back into the four form fields — used on edit-load and by the
 * back-office editor. Anything unparseable yields empty strings rather than throwing, so
 * one bad stored value cannot break the form (research.md R8).
 */
export function splitAnesthesiaWindow(window?: Partial<AnesthesiaWindow> | null): AnesthesiaWindowFields {
  const [anesthesiaStartDate, anesthesiaStartTime] = splitInstant(window?.anesthesiaStart);
  const [anesthesiaEndDate, anesthesiaEndTime] = splitInstant(window?.anesthesiaEnd);
  return { anesthesiaStartDate, anesthesiaStartTime, anesthesiaEndDate, anesthesiaEndTime };
}

function splitInstant(instant?: string | null): [string, string] {
  const value = (instant ?? '').trim();
  if (!INSTANT.test(value)) return ['', ''];
  const [date, time] = value.split('T');
  // Truncate any tolerated seconds — the form's time fields are HH:mm.
  return [date, time.slice(0, 5)];
}

/**
 * True when both endpoints are present and the end is not strictly after the start
 * (FR-016). Returns `false` for an incomplete window — incompleteness is a separate
 * failure with its own message, and reporting both at once would be noise.
 */
export function isAnesthesiaWindowInverted(fields: Partial<AnesthesiaWindowFields>): boolean {
  const window = composeAnesthesiaWindow(fields);
  if (!window) return false;
  return window.anesthesiaEnd <= window.anesthesiaStart;
}

/**
 * True for the common typo the biller needs flagged live (FR-016a): both dates are the
 * same and the start time is at or after the end time. A superset check of
 * {@link isAnesthesiaWindowInverted} restricted to the same-date case, so the error can
 * be anchored to the end *time* rather than to the window as a whole.
 */
export function isSameDateTimeInverted(fields: Partial<AnesthesiaWindowFields>): boolean {
  const startDate = (fields.anesthesiaStartDate ?? '').trim();
  const endDate = (fields.anesthesiaEndDate ?? '').trim();
  const startTime = (fields.anesthesiaStartTime ?? '').trim();
  const endTime = (fields.anesthesiaEndTime ?? '').trim();
  if (!startDate || startDate !== endDate) return false;
  if (!TIME_24H.test(startTime) || !TIME_24H.test(endTime)) return false;
  return endTime <= startTime;
}

/**
 * True when exactly one half of either date/time pair is filled — the window is
 * half-entered and cannot be persisted (FR-018). Distinguishes "nothing entered"
 * (valid: no window) from "partly entered" (invalid: prompt for the rest).
 */
export function isAnesthesiaWindowPartial(fields: Partial<AnesthesiaWindowFields>): boolean {
  const parts = [
    (fields.anesthesiaStartDate ?? '').trim(),
    (fields.anesthesiaStartTime ?? '').trim(),
    (fields.anesthesiaEndDate ?? '').trim(),
    (fields.anesthesiaEndTime ?? '').trim(),
  ];
  const filled = parts.filter(Boolean).length;
  return filled > 0 && filled < parts.length;
}

/** One validation problem with the field it should be reported against. */
export interface AnesthesiaWindowIssue {
  field: keyof AnesthesiaWindowFields;
  message: string;
}

/**
 * Every validation problem with an anesthesia window, in report order — shared by the
 * portal submit and edit schemas and the back-office case schema so all three enforce
 * one rule set (Principle 11). An empty array means the window is acceptable, including
 * the "no window at all" case, which is valid.
 *
 * Callers map `field` onto their own form path. Only the first problem found is returned,
 * since a half-entered window and an inverted one are not usefully reported together.
 */
export function getAnesthesiaWindowIssues(
  fields: Partial<AnesthesiaWindowFields>,
): AnesthesiaWindowIssue[] {
  // FR-018 — a half-entered window: point at the first blank half.
  if (isAnesthesiaWindowPartial(fields)) {
    const blank = (['anesthesiaStartDate', 'anesthesiaStartTime', 'anesthesiaEndDate', 'anesthesiaEndTime'] as const)
      .find((key) => !(fields[key] ?? '').trim());
    return blank
      ? [{ field: blank, message: 'Complete the anesthesia window — a start and end date and time are all required.' }]
      : [];
  }

  // FR-016a — the same-date typo, anchored to the end time so the biller sees it where
  // they will fix it. Checked before the general rule so its message is the specific one.
  if (isSameDateTimeInverted(fields)) {
    return [{
      field: 'anesthesiaEndTime',
      message: 'Anesthesia end time must be after start time. To record an overnight case, advance the end date.',
    }];
  }

  // FR-016 — any other inversion (an end date before the start date).
  if (isAnesthesiaWindowInverted(fields)) {
    return [{ field: 'anesthesiaEndDate', message: 'Anesthesia end must be after the start.' }];
  }

  return [];
}

/** Milliseconds in 24 hours — the threshold above which a window is flagged for review (FR-020). */
const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

/**
 * True when a complete, non-inverted window spans more than 24 hours. Such a window is
 * accepted, not blocked (FR-020) — callers surface it as a review flag.
 */
export function isAnesthesiaWindowOverADay(fields: Partial<AnesthesiaWindowFields>): boolean {
  const window = composeAnesthesiaWindow(fields);
  if (!window || window.anesthesiaEnd <= window.anesthesiaStart) return false;
  const start = new Date(`${window.anesthesiaStart}:00`).getTime();
  const end = new Date(`${window.anesthesiaEnd}:00`).getTime();
  if (Number.isNaN(start) || Number.isNaN(end)) return false;
  return end - start > TWENTY_FOUR_HOURS_MS;
}
